import { useEffect, useMemo, useRef, useState } from 'react';
import { useSubscription } from '@apollo/client';
// Api
import { ON_UPDATED_STREAM } from 'api/agora/subscriptions';
import apolloStreamClient from 'api/apolloStreamClient';
import { GetStreamBySlug_getStreamBySlug_overlayConfig_liveMatchState } from 'api/agora/types/GetStreamBySlug';

type LiveMatchStateSnapshot = GetStreamBySlug_getStreamBySlug_overlayConfig_liveMatchState;

type OnUpdatedStreamData = {
  onUpdatedStream: {
    id: string;
    slug: string | null;
    overlayConfig: {
      templateId: string | null;
      sponsorLogoKey: string | null;
      ribbonText: string | null;
      liveMatchState: LiveMatchStateSnapshot | null;
    } | null;
  } | null;
};

export type UseLiveScoreboardArgs = {
  streamId?: string | null;
  initialState?: LiveMatchStateSnapshot | null;
  skip?: boolean;
};

export const useLiveScoreboard = ({
  streamId,
  initialState,
  skip,
}: UseLiveScoreboardArgs) => {
  const [received, setReceived] = useState<LiveMatchStateSnapshot | null>(null);
  const messageSeenRef = useRef(false);
  const seededRef = useRef(false);

  useEffect(() => {
    if (seededRef.current || initialState === undefined) {
      return;
    }
    seededRef.current = true;
    if (!messageSeenRef.current) {
      setReceived(initialState);
    }
  }, [initialState]);

  useSubscription<OnUpdatedStreamData, { id: string }>(ON_UPDATED_STREAM, {
    variables: { id: streamId as string },
    client: apolloStreamClient,
    skip: skip || !streamId,
    onSubscriptionData: ({ subscriptionData }) => {
      const overlayConfig =
        subscriptionData.data?.onUpdatedStream?.overlayConfig;
      if (!overlayConfig) {
        return;
      }
      messageSeenRef.current = true;
      setReceived(overlayConfig.liveMatchState);
    },
  });

  const [displaySeconds, setDisplaySeconds] = useState<number | null>(null);

  useEffect(() => {
    setDisplaySeconds(received?.roundTimerSeconds ?? null);
  }, [received]);

  useEffect(() => {
    if (
      !received?.isTimerRunning ||
      typeof received.roundTimerSeconds !== 'number'
    ) {
      return;
    }
    const cap = received.roundDurationSeconds ?? Number.MAX_SAFE_INTEGER;
    const interval = setInterval(() => {
      setDisplaySeconds((prev) =>
        typeof prev === 'number' ? Math.min(prev + 1, cap) : prev
      );
    }, 1000);
    return () => clearInterval(interval);
  }, [received]);

  const liveMatchState = useMemo(() => {
    if (
      !received ||
      typeof received.roundTimerSeconds !== 'number' ||
      typeof displaySeconds !== 'number'
    ) {
      return received;
    }
    return { ...received, roundTimerSeconds: displaySeconds };
  }, [received, displaySeconds]);

  return { liveMatchState };
};
